import { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck, Plus, Edit3, Trash2, Save, X, Loader2, FileText,
  ChevronDown, ChevronRight, Award, AlertCircle, Upload, Eye,
} from "lucide-react";
import { adminApi } from "../api";
import { toast } from "sonner";

/** Página de manutenção dos Tipos de Certificação e docs requeridos. */
export default function AdminTiposCertificacao() {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.tiposCertificacaoList();
      setTipos(r.tipos || []);
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-6xl" data-testid="admin-tipos-certificacao">
      <div className="mb-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#A8843C] grid place-items-center text-white shrink-0">
          <Award size={20} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl text-[#1B1B1B]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}>
            Tipos de Certificação
          </h1>
          <p className="text-sm text-[#6E6754]">
            Configuração dos tipos de certificação e documentos obrigatórios por tipo.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNew(true)}
          data-testid="tipo-cert-new-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#A8843C] text-white text-sm rounded hover:bg-[#8B6E32]"
        >
          <Plus size={14} /> Novo tipo
        </button>
      </div>

      {loading && (
        <div className="p-6 text-sm text-[#978D78] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> A carregar…
        </div>
      )}

      {!loading && showNew && (
        <TipoForm
          onCancel={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); load(); }}
        />
      )}

      {!loading && tipos.length === 0 && !showNew && (
        <div className="p-6 bg-white border border-[#E6E1D6] rounded text-sm text-[#978D78]">
          Ainda não configurou nenhum tipo. Clique em &quot;Novo tipo&quot;.
        </div>
      )}

      <div className="space-y-3 mt-4">
        {tipos.map((t) => (
          <TipoCard key={t.Codigo} tipo={t} onChange={load} />
        ))}
      </div>
    </div>
  );
}


function TipoForm({ tipo = null, onCancel, onSaved }) {
  const isEdit = !!tipo;
  const [codigo, setCodigo] = useState(tipo?.Codigo || "");
  const [nome, setNome] = useState(tipo?.Nome || "");
  const [descricao, setDescricao] = useState(tipo?.Descricao || "");
  const [activo, setActivo] = useState(tipo?.Activo ?? true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!codigo.trim() || !nome.trim()) {
      toast.error("Código e nome são obrigatórios.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await adminApi.tipoCertificacaoUpdate(tipo.Codigo, { Nome: nome, Descricao: descricao || null, Activo: activo });
      } else {
        await adminApi.tipoCertificacaoCreate({ Codigo: codigo.toUpperCase(), Nome: nome, Descricao: descricao || null });
      }
      toast.success(isEdit ? "Actualizado." : "Criado.");
      onSaved?.();
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setSaving(false); }
  };

  return (
    <div
      data-testid={isEdit ? `tipo-form-edit-${tipo.Codigo}` : "tipo-form-new"}
      className="bg-white border border-[#A8843C] rounded-lg p-4 mb-3"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-[#978D78] font-semibold">
            Código
          </label>
          <input
            type="text"
            value={codigo}
            disabled={isEdit}
            onChange={(e) => setCodigo(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))}
            placeholder="EX: FORMADORES"
            data-testid="tipo-form-codigo"
            className="mt-1 w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm font-mono disabled:bg-[#F5F2EB]"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-widest text-[#978D78] font-semibold">
            Nome apresentado
          </label>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Certificação de Formadores"
            data-testid="tipo-form-nome"
            className="mt-1 w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm"
          />
        </div>
      </div>
      <div className="mt-3">
        <label className="text-[11px] uppercase tracking-widest text-[#978D78] font-semibold">
          Descrição
        </label>
        <textarea
          value={descricao || ""}
          onChange={(e) => setDescricao(e.target.value)}
          rows={2}
          data-testid="tipo-form-descricao"
          className="mt-1 w-full px-3 py-2 border border-[#E6E1D6] rounded text-sm"
        />
      </div>
      {isEdit && (
        <label className="flex items-center gap-2 mt-3 text-sm">
          <input
            type="checkbox"
            data-testid="tipo-form-activo"
            checked={activo}
            onChange={(e) => setActivo(e.target.checked)}
          />
          Activo
        </label>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          data-testid="tipo-form-cancel"
          className="px-3 py-1.5 border border-[#E6E1D6] rounded text-sm text-[#6E6754] hover:bg-[#FAF8F2]"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="tipo-form-save"
          className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-[#A8843C] text-white text-sm rounded hover:bg-[#8B6E32] disabled:opacity-60"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          Guardar
        </button>
      </div>
    </div>
  );
}


function TipoCard({ tipo, onChange }) {
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(false);
  const [addingDoc, setAddingDoc] = useState(false);

  const del = async () => {
    if (!confirm(`Desactivar tipo "${tipo.Nome}"?`)) return;
    try {
      await adminApi.tipoCertificacaoDelete(tipo.Codigo);
      toast.success("Desactivado.");
      onChange?.();
    } catch (e) { toast.error(e.message || "Erro"); }
  };

  const docsAct = (tipo.documentos || []).filter((d) => d.Activo);

  return (
    <div
      data-testid={`tipo-card-${tipo.Codigo}`}
      className={`bg-white border rounded-lg overflow-hidden ${tipo.Activo ? "border-[#E6E1D6]" : "border-red-200 opacity-70"}`}
    >
      <div className="flex items-center gap-2 px-4 py-3 bg-[#FAF8F2]">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          data-testid={`tipo-toggle-${tipo.Codigo}`}
          className="flex items-center gap-2 flex-1 text-left"
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <ShieldCheck size={14} className="text-[#A8843C]" />
          <div>
            <div className="font-semibold text-sm text-[#1B1B1B]">{tipo.Nome}</div>
            <div className="text-[11px] font-mono text-[#978D78]">{tipo.Codigo}</div>
          </div>
          {!tipo.Activo && (
            <span className="text-[10px] uppercase px-2 py-0.5 bg-red-100 text-red-700 rounded ml-2">
              Inactivo
            </span>
          )}
          <span className="ml-auto text-xs text-[#6E6754]">
            {docsAct.length} documento{docsAct.length !== 1 ? "s" : ""}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid={`tipo-edit-${tipo.Codigo}`}
          className="text-[#6E6754] hover:text-[#A8843C] p-1"
          title="Editar tipo"
        >
          <Edit3 size={14} />
        </button>
        <button
          type="button"
          onClick={del}
          data-testid={`tipo-delete-${tipo.Codigo}`}
          className="text-[#B8B0A0] hover:text-red-600 p-1"
          title="Desactivar"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {editing && (
        <div className="p-3 border-t border-[#F0EBDF]">
          <TipoForm
            tipo={tipo}
            onCancel={() => setEditing(false)}
            onSaved={() => { setEditing(false); onChange?.(); }}
          />
        </div>
      )}

      {open && !editing && (
        <div className="p-4 border-t border-[#F0EBDF]">
          {tipo.Descricao && (
            <p className="text-xs text-[#6E6754] mb-3 flex items-start gap-1">
              <AlertCircle size={11} className="mt-0.5 shrink-0" /> {tipo.Descricao}
            </p>
          )}
          <div className="space-y-1.5 mb-3">
            {docsAct.length === 0 && (
              <div className="text-sm text-[#978D78] italic">
                Sem documentos configurados. Adicione um abaixo.
              </div>
            )}
            {docsAct.map((d) => (
              <DocRow key={d.ID} doc={d} onChange={onChange} />
            ))}
          </div>
          {!addingDoc ? (
            <button
              type="button"
              onClick={() => setAddingDoc(true)}
              data-testid={`tipo-add-doc-${tipo.Codigo}`}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[#A8843C] text-[#A8843C] rounded hover:bg-[#A8843C] hover:text-white"
            >
              <Plus size={12} /> Adicionar documento
            </button>
          ) : (
            <DocForm
              tipoCodigo={tipo.Codigo}
              onCancel={() => setAddingDoc(false)}
              onSaved={() => { setAddingDoc(false); onChange?.(); }}
            />
          )}
        </div>
      )}
    </div>
  );
}


function DocRow({ doc, onChange }) {
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);

  const del = async () => {
    if (!confirm(`Remover documento "${doc.Nome}"?`)) return;
    try {
      await adminApi.tipoCertificacaoDocDelete(doc.ID);
      toast.success("Removido.");
      onChange?.();
    } catch (e) { toast.error(e.message || "Erro"); }
  };

  const uploadTemplate = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await adminApi.tipoCertificacaoDocTemplateUpload(doc.ID, file);
      toast.success(`Template ${r.ext.toUpperCase()} carregado (${Math.round(r.size / 1024)} KB).`);
      onChange?.();
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setUploading(false); }
  };

  const removeTemplate = async () => {
    if (!confirm("Remover template deste documento?")) return;
    try {
      await adminApi.tipoCertificacaoDocTemplateDelete(doc.ID);
      toast.success("Template removido.");
      onChange?.();
    } catch (e) { toast.error(e.message || "Erro"); }
  };

  if (editing) {
    return (
      <DocForm
        doc={doc}
        onCancel={() => setEditing(false)}
        onSaved={() => { setEditing(false); onChange?.(); }}
      />
    );
  }

  return (
    <div
      data-testid={`doc-row-${doc.ID}`}
      className="flex items-start gap-2 px-3 py-2 bg-[#FAF8F2] rounded text-sm"
    >
      <FileText size={13} className="text-[#A8843C] mt-1" />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[#1B1B1B]">
          {doc.Nome}
          {!doc.Obrigatorio && (
            <span className="ml-2 text-[10px] font-normal bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
              opcional
            </span>
          )}
        </div>
        <div className="text-[11px] font-mono text-[#978D78]">
          {doc.Codigo_Documento} · ordem {doc.Ordem}
        </div>
        {doc.Hint && (
          <div className="text-[11px] text-[#6E6754] mt-0.5">{doc.Hint}</div>
        )}
        <div className="flex items-center gap-2 mt-1.5">
          {doc.TemplateURL ? (
            <>
              <a
                href={doc.TemplateURL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`doc-template-view-${doc.ID}`}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 border border-[#A8843C] text-[#A8843C] rounded hover:bg-[#A8843C] hover:text-white"
              >
                <Eye size={10} /> Ver template
              </a>
              <button
                type="button"
                onClick={removeTemplate}
                data-testid={`doc-template-remove-${doc.ID}`}
                className="text-[11px] text-[#B8B0A0] hover:text-red-600"
              >
                remover
              </button>
            </>
          ) : (
            <span className="text-[11px] text-[#B8B0A0] italic">sem template</span>
          )}
          <label
            data-testid={`doc-template-upload-${doc.ID}`}
            className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded cursor-pointer ${
              uploading
                ? "bg-[#F5F2EB] text-[#B8B0A0] cursor-not-allowed"
                : "bg-[#0F1829] text-white hover:bg-[#1B2540]"
            }`}
          >
            {uploading ? <Loader2 size={10} className="animate-spin" /> : <Upload size={10} />}
            {doc.TemplateURL ? "Substituir" : "Upload template"}
            <input
              type="file"
              accept=".pdf,.docx,.doc"
              hidden
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadTemplate(f);
                e.target.value = "";
              }}
            />
          </label>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid={`doc-edit-${doc.ID}`}
        className="text-[#6E6754] hover:text-[#A8843C] p-1"
      >
        <Edit3 size={12} />
      </button>
      <button
        type="button"
        onClick={del}
        data-testid={`doc-delete-${doc.ID}`}
        className="text-[#B8B0A0] hover:text-red-600 p-1"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}


function DocForm({ tipoCodigo = null, doc = null, onCancel, onSaved }) {
  const isEdit = !!doc;
  const [codigo, setCodigo] = useState(doc?.Codigo_Documento || "");
  const [nome, setNome] = useState(doc?.Nome || "");
  const [hint, setHint] = useState(doc?.Hint || "");
  const [templateUrl, setTemplateUrl] = useState(doc?.TemplateURL || "");
  const [obrigatorio, setObrigatorio] = useState(doc?.Obrigatorio ?? true);
  const [ordem, setOrdem] = useState(doc?.Ordem ?? 0);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!codigo.trim() || !nome.trim()) {
      toast.error("Código e nome são obrigatórios.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        Nome: nome,
        Hint: hint || null,
        TemplateURL: templateUrl || null,
        Obrigatorio: obrigatorio,
        Ordem: parseInt(ordem, 10) || 0,
      };
      if (isEdit) {
        await adminApi.tipoCertificacaoDocUpdate(doc.ID, body);
      } else {
        await adminApi.tipoCertificacaoDocAdd(tipoCodigo, {
          ...body,
          Codigo_Documento: codigo.toLowerCase(),
        });
      }
      toast.success(isEdit ? "Actualizado." : "Adicionado.");
      onSaved?.();
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setSaving(false); }
  };

  return (
    <div
      data-testid={isEdit ? `doc-form-edit-${doc.ID}` : "doc-form-new"}
      className="bg-white border border-[#A8843C] rounded-lg p-3"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#978D78] font-semibold">
            Código do documento
          </label>
          <input
            type="text"
            value={codigo}
            disabled={isEdit}
            onChange={(e) => setCodigo(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
            placeholder="ex: contrato_estagio"
            data-testid="doc-form-codigo"
            className="mt-1 w-full px-2 py-1.5 border border-[#E6E1D6] rounded text-xs font-mono disabled:bg-[#F5F2EB]"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#978D78] font-semibold">
            Nome apresentado
          </label>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Contrato de estágio assinado"
            data-testid="doc-form-nome"
            className="mt-1 w-full px-2 py-1.5 border border-[#E6E1D6] rounded text-xs"
          />
        </div>
      </div>
      <div className="mb-2">
        <label className="text-[10px] uppercase tracking-widest text-[#978D78] font-semibold">
          Instruções (hint)
        </label>
        <input
          type="text"
          value={hint}
          onChange={(e) => setHint(e.target.value)}
          placeholder="Ex: Descarrega o template, assina e faz upload"
          data-testid="doc-form-hint"
          className="mt-1 w-full px-2 py-1.5 border border-[#E6E1D6] rounded text-xs"
        />
      </div>
      <div className="mb-2">
        <label className="text-[10px] uppercase tracking-widest text-[#978D78] font-semibold">
          URL do template (opcional)
        </label>
        <input
          type="text"
          value={templateUrl}
          onChange={(e) => setTemplateUrl(e.target.value)}
          placeholder="/api/public/templates/..."
          data-testid="doc-form-template"
          className="mt-1 w-full px-2 py-1.5 border border-[#E6E1D6] rounded text-xs font-mono"
        />
      </div>
      <div className="flex items-center gap-4 mb-3">
        <label className="flex items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            checked={obrigatorio}
            onChange={(e) => setObrigatorio(e.target.checked)}
            data-testid="doc-form-obrigatorio"
          />
          Obrigatório
        </label>
        <label className="flex items-center gap-1.5 text-xs">
          Ordem:
          <input
            type="number"
            min={0}
            value={ordem}
            onChange={(e) => setOrdem(e.target.value)}
            data-testid="doc-form-ordem"
            className="w-16 px-2 py-1 border border-[#E6E1D6] rounded text-xs"
          />
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          data-testid="doc-form-cancel"
          className="inline-flex items-center gap-1 px-2 py-1 border border-[#E6E1D6] rounded text-xs text-[#6E6754]"
        >
          <X size={11} /> Cancelar
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="doc-form-save"
          className="inline-flex items-center gap-1 px-3 py-1 bg-[#A8843C] text-white text-xs rounded hover:bg-[#8B6E32] disabled:opacity-60"
        >
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
          Guardar
        </button>
      </div>
    </div>
  );
}
